/**
 * useTemplates — TanStack Query wrappers around the presentation-templates
 * endpoint family (chrome / paper / writing_style).
 *
 * Why a single hook module ports both list + preferences: the StudioDrawer
 * Brand and Voice panels both need the list-by-kind result *plus* the
 * resolved preference slug to highlight the active row. Pairing the hooks
 * here keeps the Drawer call-site tight (two `useQuery`s instead of three
 * scattered across modules) and lets the editor route share the same
 * cache invalidation slot when mutations land.
 *
 * Capability gate — the BE template router (``presentation_templates.py``)
 * only requires authentication (no per-capability gate in the manifest), so
 * the list query is enabled whenever the user is logged in. We keep the
 * shape parallel to ``useAgentModes`` (``hasAccess`` flag) so the Drawer
 * code reads identically across panels; ``hasAccess`` stays ``true`` here
 * by default. If future BE work adds a ``brand_templates`` capability we
 * only need to flip the constant — call sites don't change.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getTemplatePreferences,
  listPresentationTemplates,
  setTemplatePreference,
  type PreferencesResponse,
  type TemplateKind,
  type TemplateSummary,
} from "@ando/core";

export const templatesQueryKey = (kind?: TemplateKind) =>
  ["templates", kind ?? "all"] as const;

export interface UseTemplatesResult {
  readonly templates: readonly TemplateSummary[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasAccess: boolean;
  readonly refetch: () => Promise<unknown>;
}

/**
 * List built-in + user-custom + org-custom templates, optionally filtered
 * by ``kind``. Cache key includes the kind so each panel keeps its own
 * slot; ``staleTime`` is 5 min because templates change rarely (CRUD via
 * the editor route, invalidations land via ``useTemplateMutations``).
 */
export function useTemplates(kind?: TemplateKind): UseTemplatesResult {
  // The BE template endpoints are not behind a capability gate — see file
  // header. Keep the flag wired so call sites can branch on it uniformly.
  const hasAccess = true;

  const query = useQuery<TemplateSummary[]>({
    queryKey: templatesQueryKey(kind),
    queryFn: () => listPresentationTemplates(kind),
    enabled: hasAccess,
    staleTime: 5 * 60_000,
  });

  return {
    templates: query.data ?? [],
    isLoading: hasAccess ? query.isLoading : false,
    isError: query.isError,
    error: query.error,
    hasAccess,
    refetch: () => query.refetch(),
  };
}

export const templatePreferencesQueryKey = [
  "templates",
  "preferences",
] as const;

export interface UseTemplatePreferencesResult {
  readonly data: PreferencesResponse | undefined;
  readonly isLoading: boolean;
  readonly hasAccess: boolean;
}

/**
 * Read the caller's resolved template preferences (user override + org
 * default + effective slug). Drives the "active" highlight in the
 * StudioDrawer rows.
 */
export function useTemplatePreferences(): UseTemplatePreferencesResult {
  const hasAccess = true;
  const query = useQuery<PreferencesResponse>({
    queryKey: templatePreferencesQueryKey,
    queryFn: getTemplatePreferences,
    enabled: hasAccess,
    staleTime: 60_000,
  });
  return {
    data: query.data,
    isLoading: hasAccess ? query.isLoading : false,
    hasAccess,
  };
}

/**
 * Persist a user-level default-template preference. BE returns 204 — we
 * invalidate the preferences slot so the next render reflects the new
 * effective slug. Optimistic update would race the BE resolution chain
 * (user → org → fallback), so we let the round-trip drive truth.
 */
export function useSetTemplatePreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateName: string) =>
      setTemplatePreference({ template_name: templateName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templatePreferencesQueryKey });
    },
  });
}
